import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, share, shareReplay, tap } from "rxjs/operators";
import { GlobalService } from '../_services/global.service';
import * as CryptoJS from 'crypto-js';

interface Similar {
  titles: string[];
  skills: string[];
}

export interface CompletionMessage {
  role: 'assistant' | 'user' | 'system';
  content: string;
}

export interface Prompt {
  prompt_id: number;
  prompt_text: string;
  prompt_type: 'system' | 'assistant' | 'user';
  prompt_name: string;
  created_at: string;
  prompt_description: string;
  icon?: string;
  updated_at: string;
}

export interface Message {
  message_id: number;
  prompt_id: number;
  user_id: number;
  conversation_id: string;
  message_text: string;
  message_type: 'user' | 'assistant';
  message_meta?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

export interface UnifiedData {
  text_data: string;
  cleaned_text: string;
  function: null;
  seniority: null;
  type: string;
  similar: Similar;
}

@Injectable({
  providedIn: 'root'
})
export class OpenaiService {

  unifiedRequestCache: { [key: string]: Observable<UnifiedData[] | UnifiedData> } = {};

  genAiRequesteCache :{ [key: string]: Observable<any> } = {}

  private buildUrl(path: string): string {
    return `${environment.backendurl}/${path}`;
  }

  constructor(private httpClient: HttpClient) { }

  getSimilarJobTitles(query: string): Observable<string[]> {
    const url = this.buildUrl('openai/similar-job-titles');
    return this.httpClient.get<string[]>(`${url}?jobTitle=${query}`).pipe(
      map((response: any) => Object.values(response.data)[0] as string[]),
      catchError(GlobalService.handleError('Open AI', url))
    );
  }

  /**
   * @deprecated Replaced with `getJobTitleAndSkillSuggestions`
   * @param query 
   * @returns 
   */
  getUnifiedData(query: string): Observable<UnifiedData[] | UnifiedData> {
    const url = this.buildUrl('openai/unified');
    const hash = CryptoJS.MD5(query).toString();
    if (environment.openai) {
      if (Reflect.has(this.unifiedRequestCache, hash)) {
        return this.unifiedRequestCache[hash];
      }
      const req = this.httpClient.get<any>(`${url}?query=${query}`).pipe(
        map((response: any) => {
          const d = response.data;
          if (Reflect.has(d, 'job_titles') || Reflect.has(d, 'job_title') || Reflect.has(d, 'skills') || Reflect.has(d, 'skill')) {
            const jobTitles = Reflect.has(d, 'job_titles') ? d.job_titles : [d.job_title];
            const skills = Reflect.has(d, 'skills') ? d.skills : [d.skill];
            const textData = Reflect.has(d, 'text_data') ? d.text_data : [];
            return [].concat(jobTitles, skills, textData).filter(t => t);
          }
          return d;
        }),
        catchError(GlobalService.handleError('Open AI', url)),
        share()
      );
      this.unifiedRequestCache[hash] = req;
      return req;
    } else {
      return of({ similar: { skills: null, titles: null }, text_data: null, cleaned_text: null, function: null, seniority: null, type: null })
    }
  }

  getCompletion(messages: CompletionMessage[]): Observable<Record<string, any>> {
    const url = this.buildUrl('openai/completion');
    return this.httpClient.post<string[]>(url, { prompts: messages, model: 'gpt-3.5-turbo' }).pipe(
      map((response: any) => response.data),
      catchError(GlobalService.handleError('Open AI', url))
    );
  }

  getAssistant(assistantId?: number) {
    let url = this.buildUrl('openai/assistant');
    if (assistantId) {
      url += `/${assistantId}`;
    }
    return this.httpClient.get<{ status: string, data?: Prompt[]}>(url).pipe(
      map((response) => response.data),
      catchError(GlobalService.handleError('Open AI', url))
    );
  }

  getAssistantPrompt(promptId: number) {
    const url = this.buildUrl(`openai/assistant/${promptId}`);
    return this.httpClient.get<{ status: string, data: Prompt[] }>(url).pipe(
      map((response) => response.data && response.data[0]),
      catchError(GlobalService.handleError('Open AI', url))
    );
  }

  startConversation(promptId: number, message: string, meta?: string) {
    const url = this.buildUrl(`openai/assistant/${promptId}/conversation`);
    const body = { message };
    if (meta) {
      body['meta_info'] = meta;
    }
    return this.httpClient.post<{ status: string, data: { conversation_id: string, subject?: string, body?: string } }>(url, body).pipe(
      map((response) => response.data),
      catchError(GlobalService.handleError('Open AI', url))
    );
  }

  continueConversation(promptId: number, conversationId: string, message: string, meta?: string) {
    const url = this.buildUrl(`openai/assistant/${promptId}/conversation/${conversationId}`);
    const body = { message };
    if (meta) {
      body['meta_info'] = meta;
    }
    return this.httpClient.post<{ status: string, data: { conversation_id: string, subject?: string, body?: string } }>(url, body).pipe(
      map((response) => response.data),
      catchError(GlobalService.handleError('Open AI', url))
    );
  }

  getAssistantMessages(promptId: number, conversationId: string) {
    const url = this.buildUrl(`openai/assistant/${promptId}/conversation/${conversationId}`);
    return this.httpClient.get<{ status: string, data: Message[] }>(url).pipe(
      map((response) => response.data),
      catchError(GlobalService.handleError('Open AI', url))
    );
  }


  /**
   * Used to get suggestions of skills and jobtitles from AI
   * @param data Payload for AI suggestion
   * @returns 
   */
  getJobTitleAndSkillSuggestions(data: { boolean?: string, skills?: string[], jobtitles?: string[] }) {
    const url = this.buildUrl('jtskills-suggestions');
    const hash = CryptoJS.MD5(JSON.stringify(data)).toString();

    if (this.genAiRequesteCache[hash]) {
      console.log('Returning cached observable for hash:', hash);
      return this.genAiRequesteCache[hash];
    }

    const request$ = this.httpClient.post<any>(url, data).pipe(
      tap(res => console.log('API Response:', res)),
      shareReplay(1),
      catchError((err: HttpErrorResponse) => throwError(() => err))
    );

    this.genAiRequesteCache[hash] = request$;

    return request$;
  }
}

